/**
 * Badge emblems, drawn as pixel bitmaps.
 *
 * Same technique as `components/icons.tsx` and for the same reasons — no asset
 * pack, no icon font, no glyph that renders differently per OS — but at 16x16
 * rather than 10x10, because a badge is displayed four times the size of a nav
 * icon and 10x10 reads as a blur at that scale.
 *
 * `#` is the emblem's own colour, `+` is its highlight, `.` is empty. Two
 * levels rather than one is what stops these looking like stencils: the
 * highlight is what makes a flame have a hot centre and a gem have a facet.
 *
 * Adding a motif is an entry in MOTIFS. Keep every row exactly 16 characters
 * and every grid exactly 16 rows — `assertGrid` in the dev build will tell you
 * if you miscount, which is otherwise a very annoying five minutes.
 */

export type MotifName = keyof typeof MOTIFS;

const MOTIFS = {
  /* --- Type emblems ------------------------------------------------------ */
  flame: [
    '.......##.......', '......####......', '......####......', '.....##..##.....',
    '....##....##....', '...##..++..##...', '..##..++++..##..', '..#..++++++..#..',
    '.##..++++++..##.', '.##..++++++..##.', '.##...++++...##.', '..##..++++..##..',
    '...##..++..##...', '....########....', '.....######.....', '......####......',
  ],
  droplet: [
    '.......##.......', '.......##.......', '......####......', '......####......',
    '.....######.....', '.....##..##.....', '....##....##....', '....##.++.##....',
    '...##..++..##...', '...##.++++.##...', '..##..++++..##..', '..##..++++..##..',
    '..##...++...##..', '...##......##...', '....########....', '.....######.....',
  ],
  leaf: [
    '..............##', '............####', '..........######', '........###++###',
    '......###++++###', '....###++++++###', '..###++++++++###', '.##++++++++++###',
    '.##++++++++++##.', '.###++++++++##..', '..###++++++##...', '...###++++##....',
    '....###++##.....', '.##...####......', '..##..##........', '...####.........',
  ],
  bolt: [
    '.........###....', '........####....', '.......####.....', '......####......',
    '.....####.......', '....####........', '...####...##....', '..####++######..',
    '.####++########.', '.###########....', '........####....', '.......####.....',
    '......####......', '.....####.......', '....####........', '...###..........',
  ],
  crystal: [
    '.......##.......', '......####......', '.....##++##.....', '....##++++##....',
    '...##++++++##...', '..##++++++++##..', '.##++++++++++##.', '###++++++++++###',
    '.##++++++++++##.', '..##++++++++##..', '...##++++++##...', '....##++++##....',
    '.....##++##.....', '......####......', '.......##.......', '................',
  ],
  fist: [
    '................', '...####..####...', '..######.#####..', '.##++##.##++###.',
    '.##++#####++###.', '.##++++++++++##.', '##++++++++++++##', '##++++++++++++##',
    '##++++++++++++##', '.##++++++++++##.', '.##++++++++++##.', '..############..',
    '..############..', '...##########...', '....########....', '................',
  ],
  flask: [
    '.....######.....', '.....#....#.....', '.....#....#.....', '.....#....#.....',
    '....##....##....', '....#......#....', '...##......##...', '...#..++++..#...',
    '..##.++++++.##..', '..#.++++++++.#..', '.##.++++++++.##.', '.#.++++++++++.#.',
    '.#.++++++++++.#.', '.##.++++++++.##.', '..############..', '...##########...',
  ],
  mountain: [
    '................', '.......##.......', '......####......', '......#++#......',
    '.....##++##.....', '.....#++++#.....', '....##++++##....', '...##++++++##...',
    '..##++####++##..', '..#++##..##++#..', '.##++#....#++##.', '.#++##....##++#.',
    '##++#......#++##', '#++##........###', '################', '################',
  ],
  wing: [
    '................', '..##............', '.####...........', '######..........',
    '##++####........', '##++++####......', '.##++++++####...', '.##++++++++####.',
    '..##++++++++####', '..##++++++++##..', '...##++++++##...', '...##++++##.....',
    '....##++##......', '....####........', '.....##.........', '................',
  ],
  eye: [
    '................', '................', '....########....', '..####++++####..',
    '.###++++++++###.', '.##++++####++##.', '##+++##++##+++##', '##+++#++++#+++##',
    '##+++#++++#+++##', '##+++##++##+++##', '.##++++####++##.', '.###++++++++###.',
    '..####++++####..', '....########....', '................', '................',
  ],
  beetle: [
    '.....##..##.....', '......#..#......', '...##########...', '..############..',
    '.###++####++###.', '.##++++##++++##.', '##++++++##++++##', '##++++++##++++##',
    '##++++++##++++##', '.##+++++##+++##.', '.###+++####+++#.', '..############..',
    '...##########...', '....########....', '..##........##..', '.##..........##.',
  ],
  boulder: [
    '................', '......####......', '....########....', '...##++++++##...',
    '..##++++++++##..', '.##++++++++++##.', '##++++++++++++##', '##++++++++++++##',
    '##++++++++++++##', '.##++++++++++##.', '.##++++++++++##.', '..############..',
    '..############..', '...##########...', '................', '................',
  ],
  wisp: [
    '.....######.....', '...##########...', '..############..', '.###++####++###.',
    '.##++##++##++##.', '##++##++++##++##', '##++##++++##++##', '##++++++++++++##',
    '##++++++++++++##', '##++++++++++++##', '##++++++++++++##', '##++++++++++++##',
    '##++++++++++++##', '#.##.##.##.##.##', '..#...#...#...#.', '................',
  ],
  claw: [
    '..##.......##...', '..###.....###...', '..####...####...', '..#+###.###+#...',
    '..#++#####++#...', '..#++++#++++#...', '..##+++++++##...', '...##+++++##....',
    '....##+++##.....', '.....#####......', '.....##+##......', '....##+++##.....',
    '...##+++++##....', '..##+++++++##...', '..###########...', '...#########....',
  ],
  moon: [
    '......######....', '....####++++##..', '...###++++++###.', '..###++++++++###',
    '.###++++++++++##', '.##++++++++++###', '##++++++++++###.', '##++++++++###...',
    '##++++++++###...', '##++++++++++###.', '.##++++++++++###', '.###++++++++++##',
    '..###++++++++###', '...###++++++###.', '....####++++##..', '......######....',
  ],
  gear: [
    '....##....##....', '...####..####...', '...############.', '..#####++#####..',
    '.####++++++####.', '####++++++++####', '##++++####++++##', '##+++##..##+++##',
    '##+++##..##+++##', '##++++####++++##', '####++++++++####', '.####++++++####.',
    '..#####++#####..', '...############.', '...####..####...', '....##....##....',
  ],
  sparkle: [
    '.......##.......', '.......##.......', '......####......', '......####......',
    '..#...####...#..', '..##..####..##..', '...##.####.##...', '..############..',
    '..############..', '...##.####.##...', '..##..####..##..', '..#...####...#..',
    '......####......', '......####......', '.......##.......', '.......##.......',
  ],
  ring: [
    '.....######.....', '...##########...', '..####....####..', '.###........###.',
    '.##..........##.', '##............##', '##............##', '##............##',
    '##............##', '##............##', '##............##', '.##..........##.',
    '.###........###.', '..####....####..', '...##########...', '.....######.....',
  ],

  /* --- Region and system emblems ----------------------------------------- */
  tower: [
    '.......##.......', '......####......', '.....##++##.....', '....##++++##....',
    '....#++++++#....', '....##++++##....', '.....######.....', '....########....',
    '....#++++++#....', '....#++++++#....', '...##++++++##...', '...#++++++++#...',
    '..##++++++++##..', '..#++++++++++#..', '.##############.', '################',
  ],
  bell: [
    '.......##.......', '......####......', '.....######.....', '....##++++##....',
    '...##++++++##...', '..##++++++++##..', '..#++++++++++#..', '.##++++++++++##.',
    '.#++++++++++++#.', '##++++++++++++##', '##++++++++++++##', '################',
    '.....######.....', '......####......', '.......##.......', '................',
  ],
  book: [
    '................', '.##..######..##.', '####++++++++####', '##++++++++++++##',
    '##++##++++##++##', '##++++++++++++##', '##++##++++##++##', '##++++++++++++##',
    '##++##++++##++##', '##++++++++++++##', '##++##++++##++##', '##++++++++++++##',
    '####++++++++####', '.##############.', '..############..', '................',
  ],
  ball: [
    '....########....', '..############..', '.####++++++####.', '###++++++++++###',
    '##++++++++++++##', '##++++++++++++##', '################', '##....####....##',
    '##....####....##', '################', '##++++####++++##', '##++++####++++##',
    '###++++++++++###', '.####++++++####.', '..############..', '....########....',
  ],
  prism: [
    '.......##.......', '......####......', '.....##++##.....', '....##++++##....',
    '...##++++++##...', '..##++++++++##..', '.##++++++++++##.', '##++++++++++++##',
    '.##++++++++++##.', '..##++++++++##..', '...##++++++##...', '....##++++##....',
    '.....##++##.....', '......####......', '.......##.......', '................',
  ],
  egg: [
    '.......##.......', '......####......', '.....######.....', '....########....',
    '...##++++++##...', '..##++++++++##..', '.##++++++++++##.', '##++++++++++++##',
    '##++++++++++++##', '##++++++++++++##', '##++++++++++++##', '.##++++++++++##.',
    '..##++++++++##..', '...##########...', '....########....', '.....######.....',
  ],
  basket: [
    '................', '....########....', '..##++++++++##..', '.##..........##.',
    '################', '##++##++##++++##', '##++##++##++++##', '##++##++##++++##',
    '.##++##++##++##.', '.##++##++##++##.', '.##++##++##++##.', '..############..',
    '..############..', '...##########...', '................', '................',
  ],
  coin: [
    '....########....', '..############..', '.####++++++####.', '###++++++++++###',
    '##++++####++++##', '##+++##..##+++##', '##+++#....#+++##', '##+++#....#+++##',
    '##+++#....#+++##', '##+++##..##+++##', '##++++####++++##', '###++++++++++###',
    '.####++++++####.', '..############..', '....########....', '................',
  ],
  flower: [
    '.....######.....', '...##++++++##...', '..##++++++++##..', '.##++++++++++##.',
    '###++++++++++###', '#++##++++++##++#', '#++##++##++##++#', '#++++##++##++++#',
    '#++++##++##++++#', '#++##++##++##++#', '#++##++++++##++#', '###++++++++++###',
    '.##++++++++++##.', '..##++++++++##..', '....########....', '................',
  ],
  tree: [
    '.....######.....', '...##++++++##...', '..##++++++++##..', '.##++++++++++##.',
    '##++++++++++++##', '##++++++++++++##', '.##++++++++++##.', '..###++##++###..',
    '....##++++##....', '.....##++##.....', '.....##++##.....', '.....##++##.....',
    '....##++++##....', '...##++++++##...', '..############..', '................',
  ],
  lantern: [
    '.......##.......', '......####......', '.....##..##.....', '....########....',
    '...##++++++##...', '..##++++++++##..', '.##++++++++++##.', '.#++++++++++++#.',
    '.#++++++++++++#.', '.#++++++++++++#.', '.##++++++++++##.', '..##++++++++##..',
    '...##++++++##...', '....########....', '......####......', '.......##.......',
  ],
  gate: [
    '################', '################', '..##........##..', '..##........##..',
    '.####......####.', '.####......####.', '.#++#......#++#.', '.#++#......#++#.',
    '.#++#......#++#.', '.#++#......#++#.', '.#++#......#++#.', '.#++#......#++#.',
    '.#++#......#++#.', '.#++#......#++#.', '####........####', '################',
  ],
  heart: [
    '................', '..####....####..', '.######..######.', '################',
    '##++++######++##', '##++++++++++++##', '##++++++++++++##', '.##++++++++++##.',
    '.##++++++++++##.', '..##++++++++##..', '...##++++++##...', '....##++++##....',
    '.....##++##.....', '......####......', '.......##.......', '................',
  ],
  banner: [
    '################', '##++++++++++++##', '##++##++++##++##', '##++++++++++++##',
    '##++##++++##++##', '##++++++++++++##', '##++##++++##++##', '##++++++++++++##',
    '##++++++++++++##', '.##++++++++++##.', '..##++++++++##..', '...##++++++##...',
    '....##++++##....', '.....##++##.....', '......####......', '.......##.......',
  ],
  stall: [
    '..############..', '.##++##++##++##.', '.##++##++##++##.', '################',
    '..############..', '..##........##..', '..##..####..##..', '..##..#++#..##..',
    '..##..#++#..##..', '..##..#++#..##..', '..##..####..##..', '..##........##..',
    '..############..', '..############..', '................', '................',
  ],
  swap: [
    '................', '.....##.........', '....####........', '...######.......',
    '..###..##.......', '.###...##.......', '################', '################',
    '################', '################', '.......##...###.', '.......##..###..',
    '.......##.###...', '.......######...', '........####....', '.........##.....',
  ],
  /* --- Ribbon and Hall of Fame emblems ----------------------------------- */
  // Shared with badges deliberately: a motif is a picture, not a badge part,
  // and one library means a ribbon and a badge that mean the same thing look
  // related rather than being drawn twice by two people.
  star: [
    '.......##.......', '.......##.......', '......####......', '......####......',
    '.....##++##.....', '.....##++##.....', '#####++++++#####', '.###++++++++###.',
    '..##++++++++##..', '...##++++++##...', '..###++++++###..', '..##++++++++##..',
    '.##++##..##++##.', '.##.###..###.##.', '##...##..##...##', '................',
  ],
  berry: [
    '.......##.......', '......##........', '.....##..###....', '....##..#####...',
    '...####..###....', '..##++++##......', '.##++++++##.....', '##++++++++##....',
    '##++++++++##....', '##++++++++##..##', '.##++++++##..##.', '..##++++##..##..',
    '...######..##...', '....####..##....', '.....##..##.....', '.........##.....',
  ],
  crown: [
    '................', '.##..........##.', '.##....##....##.', '.##...####...##.',
    '.###..####..###.', '.###.##++##.###.', '.####++++++####.', '.##++++++++++##.',
    '.##++++++++++##.', '.##++++++++++##.', '.##++++++++++##.', '.##############.',
    '.##############.', '.##..##..##..##.', '.##############.', '................',
  ],
  sun: [
    '.......##.......', '...#...##...#...', '....#..##..#....', '.....######.....',
    '....########....', '..##++++++++##..', '#.##++++++++##.#', '###++++++++++###',
    '###++++++++++###', '#.##++++++++##.#', '..##++++++++##..', '....########....',
    '.....######.....', '....#..##..#....', '...#...##...#...', '.......##.......',
  ],
  wreath: [
    '.....######.....', '...##++++++##...', '..##++####++##..', '.##++##....##++#',
    '.##+##......##+#', '##++#........#++', '##++#........#++', '##++#........#++',
    '##++#........#++', '##++#........#++', '.##+##......##+#', '.##++##....##++#',
    '..##++####++##..', '...##++++++##...', '.....##..##.....', '....###..###....',
  ],
  people: [
    '....##....##....', '...####..####...', '...####..####...', '....##....##....',
    '................', '..######..######', '.########.######', '##++++++########',
    '##++++++++######', '##++++++++++####', '##++++++++++##..', '##++++++++++##..',
    '##++++++++++##..', '.##++++++++##...', '..##########....', '..##########....',
  ],
  camera: [
    '................', '.....######.....', '.....##..##.....', '################',
    '##............##', '##...######...##', '##..##++++##..##', '##.##++++++##.##',
    '##.##++++++##.##', '##.##++++++##.##', '##..##++++##..##', '##...######...##',
    '##............##', '##..........####', '################', '................',
  ],
  relic: [
    '................', '....########....', '....##....##....', '.....######.....',
    '.....##++##.....', '....##++++##....', '...##++++++##...', '..##++++++++##..',
    '.##++++++++++##.', '.##++++++++++##.', '.##++++++++++##.', '..##++++++++##..',
    '...##++++++##...', '....########....', '..############..', '................',
  ],
  shield: [
    '.##############.', '.##++++++++++##.', '##++++++++++++##', '##++##++++##++##',
    '##++++++++++++##', '##++++++++++++##', '##++++++++++++##', '##++++++++++++##',
    '.##++++++++++##.', '.##++++++++++##.', '..##++++++++##..', '...##++++++##...',
    '....##++++##....', '.....##++##.....', '......####......', '.......##.......',
  ],
  list: [
    '.##############.', '.##............#', '.##.####..####.#', '.##............#',
    '.##.####..####.#', '.##............#', '.##.####..####.#', '.##............#',
    '.##.####..####.#', '.##............#', '.##.####..####.#', '.##............#',
    '.##.####..####.#', '.##............#', '.##############.', '................',
  ],
} as const;

/** Empty grid, so an unknown motif renders as a blank plate rather than crashing. */
const BLANK = Array.from({ length: 16 }, () => '.'.repeat(16));

export function motifRows(name: string): readonly string[] {
  return (MOTIFS as Record<string, readonly string[]>)[name] ?? BLANK;
}

export const MOTIF_NAMES = Object.keys(MOTIFS) as MotifName[];

/**
 * Renders a motif as two layers of <rect>s inside a 16x16 viewBox.
 *
 * Runs of adjacent pixels are merged into single rects — a 16x16 grid is up to
 * 256 elements per badge and a collection page shows forty of them, which is
 * ten thousand DOM nodes if each pixel is its own rect. Merging typically cuts
 * that by four or five times, and costs nothing at build time.
 */
export function MotifLayer({
  motif,
  fill,
  highlight,
}: {
  motif: string;
  fill: string;
  highlight: string;
}) {
  const rows = motifRows(motif);
  const rects: Array<{ x: number; y: number; w: number; colour: string }> = [];

  rows.forEach((row, y) => {
    let runStart = -1;
    let runChar = '';
    const flush = (end: number) => {
      if (runStart < 0) return;
      rects.push({
        x: runStart,
        y,
        w: end - runStart,
        colour: runChar === '+' ? highlight : fill,
      });
      runStart = -1;
      runChar = '';
    };

    for (let x = 0; x < row.length; x += 1) {
      const cell = row[x]!;
      if (cell === '.') {
        flush(x);
        continue;
      }
      if (cell !== runChar) {
        flush(x);
        runStart = x;
        runChar = cell;
      }
    }
    flush(row.length);
  });

  return (
    <>
      {rects.map((rect, index) => (
        <rect
          key={index}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={1}
          fill={rect.colour}
          shapeRendering="crispEdges"
        />
      ))}
    </>
  );
}
